import { AppFrame, CommandPalette } from '@kanos/ui';
import {
  AccountMenu,
  AppFooter,
  MaintenanceBanner,
  PlaceLinks,
  profileCommands,
  SectionNav,
  type Me,
  type NavLinkRenderer,
  type NavSection,
  type Place,
} from '@kanos/web-core';
import {
  Blocks,
  Building2,
  CircleHelp,
  FileSignature,
  ListTodo,
  Megaphone,
  Users,
  Wrench,
} from 'lucide-react';
import type { ReactNode } from 'react';

/** The places of the platform app, by the path in the address' hash. */
const PLACES: readonly Place[] = [
  { to: '/', label: 'Partner-Apps', icon: Blocks },
  { to: '/firms', label: 'Kanzleien', icon: Building2 },
  { to: '/tasks', label: 'Aufgaben', icon: ListTodo },
  { to: '/contract-fields', label: 'Vertragsangaben', icon: FileSignature },
  { to: '/team', label: 'Plattform-Team', icon: Users },
  { to: '/maintenance', label: 'Wartung', icon: Wrench },
  { to: '/announcements', label: 'Neuigkeiten', icon: Megaphone },
  { to: '/help', label: 'Häufige Fragen', icon: CircleHelp },
];

/** The platform app has no router: its pages are the hash of the address (`#/team`). */
const hashLink: NavLinkRenderer = ({ to, children, ...props }) => (
  <a href={`#${to}`} {...props}>
    {children}
  </a>
);

/** The frame of the platform app: the same bar, places and account as the firm app. */
export function PlatformShell({
  me,
  pathname,
  sections,
  children,
}: {
  me: Me;
  pathname: string;
  sections: readonly NavSection[];
  children: ReactNode;
}) {
  const section = sections.find((s) => pathname.startsWith(`${s.base}/`));
  const open = (path: string) => window.location.assign(`#${path}`);
  const go = (path: string) => () => open(path);
  const search = (
    <CommandPalette
      commands={[
        ...PLACES.map(({ to, label, icon }) => ({
          id: to,
          label,
          group: 'Bereiche',
          icon,
          onAction: go(to),
        })),
        ...profileCommands(go),
      ]}
    />
  );
  const account = <AccountMenu me={me} context="Kanos-Plattform" onProfile={go('/profile')} />;
  return (
    <AppFrame
      brand={
        <a
          href="#/"
          aria-label="Zur Startseite"
          className="grid rounded-sm leading-tight outline-none focus-visible:outline-2 focus-visible:outline-focus"
        >
          <span className="text-xl font-bold tracking-[-0.03em]">kanos</span>
          <span className="text-sm text-muted">Plattform</span>
        </a>
      }
      places={(variant) => (
        <PlaceLinks
          link={hashLink}
          places={PLACES}
          pathname={pathname}
          variant={variant}
          go={open}
        />
      )}
      section={section && <SectionNav link={hashLink} section={section} pathname={pathname} />}
      actions={
        <>
          {search}
          {account}
        </>
      }
      mobileActions={
        <>
          {search}
          {account}
        </>
      }
      notice={<MaintenanceBanner />}
      footer={<AppFooter />}
    >
      {children}
    </AppFrame>
  );
}
